import React, { useCallback, memo, useState, useEffect } from 'react';
import { stubTrue } from 'lodash-es';
import { useLoadingCoordinator } from 'site-modules/personalized-search/hooks/use-loading-coordinator';

export const withComponentLoader =
  ({ skeleton: Skeleton, checkDataLoaded = stubTrue }) =>
  Component => {
    const ComponentLoader = memo(props => {
      const { setLoadingStatus, checkPreviousComponentsLoaded } = useLoadingCoordinator();
      const { componentPath, componentParams } = props;
      const [rendered, setRendered] = useState(false);

      const isDataLoaded = checkDataLoaded(props);

      useEffect(() => {
        if (isDataLoaded && rendered) {
          setLoadingStatus(componentPath);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [isDataLoaded, rendered]);

      useEffect(
        () => () => {
          setRendered(false);
        },
        [componentParams]
      );

      const handleComponentRendered = useCallback(() => {
        setRendered(componentPath);
      }, [componentPath]);

      const checkSkeletonState = () =>
        !componentParams || !checkPreviousComponentsLoaded(componentPath) || !isDataLoaded;

      const checkComponentDisplayState = () => checkPreviousComponentsLoaded(componentPath) && isDataLoaded;

      const shouldRenderSkeleton = checkSkeletonState();
      const showComponent = checkComponentDisplayState();

      return (
        <>
          {shouldRenderSkeleton && <Skeleton {...props} />}
          {componentParams && (
            <div className="w-100" hidden={!showComponent}>
              <Component {...props} hidden={!showComponent} onRendered={handleComponentRendered} />
            </div>
          )}
        </>
      );
    });

    ComponentLoader.displayName = `withComponentLoader(${Component.displayName || Component.name})`;

    return ComponentLoader;
  };
